import { CommonModule } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CourseManagementService } from '@edtech-ui/core/services/course-management/course-management.service';
import { QuizManagementService } from '@edtech-ui/core/services/quiz-management/quiz-management.service';
import { environment } from 'environments/environment';
import { Subscription } from 'rxjs';

@Component({
	selector: 'app-edit-quiz-dialog-box',
	standalone: true,
	imports: [CommonModule, ReactiveFormsModule, FormsModule],
	templateUrl: './edit-quiz-dialog-box.component.html',
	styleUrl: './edit-quiz-dialog-box.component.scss'
})
export class EditQuizDialogBoxComponent implements OnInit, OnDestroy {

	mode: string = 'course';
	shuffleQuestions: boolean = false;
	shuffleOptions: boolean = false;
	isProctored = false;
	isGroupDropdownOpen = false;
	selectedGroups: string[] = [];
	selectedOptions: string[] = [];
	quizForm: FormGroup;
	loading = false;
	visiblePages: number[] = [];
	totalQuiz: number = 0;
	pageCourse = 1;
	limitCourse = 10;
	totalPagesCourse = 0;
	courses: { id: string; name: string }[] = [];
	selectedCourse: { id: string; name: string } | null = null;
	classroomId = environment.classroomId;
	selectedCourseId: string | null = null;
	subscription = new Subscription();


	constructor(
		@Inject(MAT_DIALOG_DATA) public data: any,
		public dialogRef: MatDialogRef<EditQuizDialogBoxComponent>,
		private quizService: QuizManagementService,
		private courseService: CourseManagementService
	) {
		this.quizForm = new FormGroup({
			quiz_name: new FormControl(''),
			//description: new FormControl(''),
			time_limit: new FormControl(0),
			hh: new FormControl(0),
			mm: new FormControl(0),
			ss: new FormControl(0),
			attempts_allowed: new FormControl(1),
			passing_score: new FormControl(0),
			questions_per_quiz: new FormControl(0),
			shuffle_questions: new FormControl(false),
			shuffle_options: new FormControl(false),
			mode: new FormControl('course'),
			//start_date_time: new FormControl(null),
			//end_date_time: new FormControl(null),
			course_id: new FormControl(null),
			isProctored: new FormControl(false)
		});
	}


	ngOnInit(): void {
		if (this.data?.quizId) {
			this.getQuizById(this.data.quizId);
		}
		this.getAdminCourses();
	}


	closeDialog() {
		this.dialogRef.close();
	}


	userGroups: string[] = [
		'Testing 1',
		'Testing 2',
		'Testing 3',
		'Testing 4',
		'Testing 5'
	];


	toggleGroupDropdown() {
		this.isGroupDropdownOpen = !this.isGroupDropdownOpen;
	}


	onSelectGroup(group: string) {
		if (this.selectedGroups.includes(group)) {
			this.selectedGroups = this.selectedGroups.filter(g => g !== group);
		} else {
			this.selectedGroups.push(group);
		}
	}


	proctorOptions: string[] = [
		'Detect tab switch',
		'Restrict new tabs / window switch',
		'Enable camera access',
		'Face detection',
		'Multiple person detection',
		'No talking detection',
		'Eye movement tracking'
	];


	toggleOption(option: string) {
		if (this.selectedOptions.includes(option)) {
			this.selectedOptions = this.selectedOptions.filter(o => o !== option);
		} else {
			this.selectedOptions.push(option);
		}
	}


	getQuizById(quizId: string) {
		this.subscription.add(
			this.quizService.getQuizById(quizId).subscribe({
				next: (res) => {
					if (res?.Success && res.Data) {
						const quiz = res.Data;
						const t = this.secondsToHMS(quiz.time_limit || 0);
						this.selectedCourseId = quiz.course_id;
						this.quizForm.patchValue({
							quiz_name: quiz.quiz_name,
							description: quiz.description || '',
							hh: t.hh, mm: t.mm, ss: t.ss,
							attempts_allowed: quiz.attempts_allowed,
							passing_score: quiz.passing_score,
							questions_per_quiz: quiz.questions_per_quiz,
							shuffle_questions: quiz.shuffle_questions,
							shuffle_options: quiz.shuffle_options,
							mode: quiz.mode,
							isProctored: quiz.proctoring_config?.enable_proctoring || false
						});
						this.isProctored = quiz.proctoring_config?.enable_proctoring || false;
						this.selectedGroups = quiz.assigned_groups || [];
						this.selectedOptions = this.mapProctoring(quiz.proctoring_config);
					}
				}
			})
		);
	}


	mapProctoring(config: any): string[] {
		if (!config) return [];
		const map: any = {
			detect_tab_switch: 'Detect tab switch',
			restrict_new_tabs: 'Restrict new tabs / window switch',
			enable_camera: 'Enable camera access',
			face_detection: 'Face detection',
			multiple_person_detection: 'Multiple person detection',
			no_talking_detection: 'No talking detection',
			eye_movement_tracking: 'Eye movement tracking'
		};
		return Object.keys(config).filter(key => config[key] === true && map[key]).map(key => map[key]);
	}



	saveQuiz() {
		const quizId = this.data.quizId;
		const form = this.quizForm.value;
		const time_limit_seconds = this.hmsToSeconds(form.hh, form.mm, form.ss);

		const payload = {
			quiz_name: form.quiz_name,
			//description: form.description,
			time_limit: time_limit_seconds,
			attempts_allowed: form.attempts_allowed,
			passing_score: form.passing_score,
			questions_per_quiz: form.questions_per_quiz,
			shuffle_questions: form.shuffle_questions,
			shuffle_options: form.shuffle_options,
			mode: form.mode,
			course_id: form.course_id,
			assigned_groups: this.selectedGroups,
			assigned_users: [],
			proctoring_config: this.buildProctoringPayload()
		};

		this.subscription.add(
			this.quizService.postUpdateQuizNameTime(quizId, payload).subscribe({
				next: (response) => {
					if (response?.Success) {
						this.dialogRef.close(true);
					}
				}
			})
		);
	}


	buildProctoringPayload() {
		const selected = this.selectedOptions || [];
		return {
			enable_proctoring: this.isProctored,
			detect_tab_switch: selected.includes('Detect tab switch'),
			restrict_new_tabs: selected.includes('Restrict new tabs / window switch'),
			enable_camera: selected.includes('Enable camera access'),
			face_detection: selected.includes('Face detection'),
			multiple_person_detection: selected.includes('Multiple person detection'),
			no_talking_detection: selected.includes('No talking detection'),
			eye_movement_tracking: selected.includes('Eye movement tracking'),
			max_warnings: 3
		};
	}


	private secondsToHMS(totalSeconds: number) {
		const hh = Math.floor(totalSeconds / 3600);
		const mm = Math.floor((totalSeconds % 3600) / 60);
		const ss = totalSeconds % 60;
		return { hh, mm, ss };
	}


	private hmsToSeconds(hh: number, mm: number, ss: number): number {
		return (Number(hh || 0) * 3600) + (Number(mm || 0) * 60) + Number(ss || 0);
	}


	getAdminCourses(): void {
		if (this.loading) return;
		this.loading = true;
		this.subscription.add(
			this.courseService.getCourses(this.classroomId, this.pageCourse, this.limitCourse, 'created_at', 'desc', 'all').subscribe({
				next: response => {
					this.loading = false;
					if (response?.Success) {
						const publishedCourses = response.Data.data.filter((course: any) => course.status === 'Published')
							.map((course: any) => ({
								id: course.course_id,
								name: course.course_title,
								status: course.status
							}));
						this.courses = [...this.courses, ...publishedCourses];
						if (this.selectedCourseId) {
							this.quizForm.patchValue({ course_id: this.selectedCourseId });
						}
						this.pageCourse = response.Data.page + 1;
						this.totalPagesCourse = response.Data.total_pages;
					}
				},
				error: () => {
					this.loading = false;
				}
			})
		);
	}


	handleTimeInput(event: any, max: number) {
		let value = event.target.value;
		value = value.replace(/[^0-9]/g, '');

		if (value.length > 2) {
			value = value.slice(0, 2);
		}
		if (value !== '' && Number(value) > max) {
			value = max.toString();
		}
		event.target.value = value;
	}


	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}


}